import { BrandMark } from '@/components/ui/BrandMark';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import type { IconName, SectionId } from '@/types';
import moeCrest from '@/assets/moe-oman.webp';
import visionCrest from '@/assets/oman-vision-2040.webp';
import styles from './Home.module.css';

const AUDIENCE: readonly { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'person',
    title: 'للمعلمين',
    body: 'استخدم أدوات الذكاء الاصطناعي لإنشاء دروس تفاعلية وجذابة بسهولة وسرعة.',
  },
  {
    icon: 'star',
    title: 'للطلبة',
    body: 'تعلم كيفية استخدام تقنيات الذكاء الاصطناعي في مشاريعك الدراسية والعملية.',
  },
  {
    icon: 'check_circle',
    title: 'مجاني وسهل',
    body: 'جميع الأدوات المقترحة مجانية وسهلة الاستخدام بدون تعقيدات تقنية.',
  },
];

/** `src: null` keeps the reserved plate until that emblem is supplied. */
/** Official emblems; each carries its own wording, so the alt text is all the
    description they need. */
const CRESTS = [
  { src: moeCrest, alt: 'شعار وزارة التعليم — سلطنة عُمان', width: 600, height: 220 },
  { src: visionCrest, alt: 'شعار رؤية عُمان 2040', width: 672, height: 380 },
] as const;

export interface HomeProps {
  readonly onNavigate: (section: SectionId) => void;
  readonly titleId: string;
}

export function Home({ onNavigate, titleId }: HomeProps) {
  return (
    <>
      <div className={styles.hero}>
        <BrandMark size={168} variant="full" animated title="شعار المنصة" className={styles.mark} />

        <p className={styles.eyebrow}>منصة تعليمية عُمانية</p>

        <h1 className={styles.title} id={titleId}>
          اصنع أثرك الرقمي
        </h1>

        <p className={styles.lede}>
          منصة تعليمية أنشأتها آية سعود لمساعدة المعلمين والطلبة على كيفية عمل درس رقمي أو مشروع
          رقمي باستخدام الذكاء الاصطناعي بسهولة، بدون الحاجة لخبرة تقنية.
        </p>

        <div className={styles.actions}>
          <Button size="large" onClick={() => onNavigate('tools')}>
            تصفّح الأدوات
          </Button>
          <Button variant="quiet" onClick={() => onNavigate('qa')}>
            اطرح سؤالك
          </Button>
        </div>
      </div>

      <div className={styles.audience}>
        {AUDIENCE.map((card) => (
          <section key={card.title} className={styles.card}>
            <Icon name={card.icon} size={26} className={styles.cardIcon} />
            <h2 className={styles.cardTitle}>{card.title}</h2>
            <p className={styles.cardBody}>{card.body}</p>
          </section>
        ))}
      </div>

      <div className={styles.vision}>
        <p className={styles.visionLabel}>بالتوافق مع رؤية عُمان 2040</p>
        <div className={styles.crests}>
          {CRESTS.map((crest) => (
            <div key={crest.alt} className={styles.crest}>
              <img
                className={styles.crestImage}
                src={crest.src}
                alt={crest.alt}
                width={crest.width}
                height={crest.height}
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
